import {
  Component,
  inject,
  viewChild,
  signal,
  ElementRef,
  OnDestroy,
  AfterViewInit,
  computed,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Sort } from '@angular/material/sort';

import { IntuneDevice } from '../../../../../Models/Intune/intune-device.model';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AvTableLoadingBarComponent } from '../../../../Shared/Components/loading-bar/table-loading-bar.component';
import { AvTableCopyCellComponent } from '../../../../Shared/Components/table-copy-cell/table-copy-cell.component';
import { ListDevicesFacade } from '../../../../../Facades/Intune/list-devices.facade';
import { AvBoolCellComponent } from '../../../../Shared/Components/av-bool-cell/av-bool-cell.component';

@Component({
  selector: 'app-all-devices-table',
  standalone: true,
  imports: [
    COMMON_IMPORTS,
    AvTableLoadingBarComponent,
    AvTableCopyCellComponent,
    AvBoolCellComponent,
  ],
  templateUrl: './all-devices-table.component.html',
  styleUrls: [
    './all-devices-table.component.scss',
    '../../../../../../assets/theme/cutom/_av-table.scss',
  ],
})
export class AllDevicesTableComponent implements AfterViewInit, OnDestroy {
  public facade = inject(ListDevicesFacade);

  readonly searchTerm = this.facade.searchTerm;
  readonly orderBy = this.facade.orderBy;
  readonly order = this.facade.order;

  private _orderBy = signal('deviceName');
  private _order = signal<'asc' | 'desc'>('asc');

  readonly devices = this.facade.devices;
  readonly count = this.facade.count;
  readonly hasMore = this.facade.hasMore;
  readonly isLoading = this.facade.isLoading;
  readonly isLoadingMore = this.facade.isLoadingMore;
  readonly isBottomReached = signal(false);

  dataSource = computed(() => new MatTableDataSource<IntuneDevice>(this.devices()));
  displayedColumns: string[] = [
    'index',
    'deviceName',
    'user',
    'email',
    'operatingSystem',
    'complianceState',
    'agent',
    'action',
  ];

  bottomDetector = viewChild<ElementRef>('bottomDetector');
  tableWrapper = viewChild<ElementRef>('tableWrapper');

  private _observer: IntersectionObserver | null = null;

  ngAfterViewInit() {
    this.setupScrollObserver();
  }

  ngOnDestroy() {
    if (this._observer) this._observer.disconnect();
    this.facade.clear();
  }

  private setupScrollObserver() {
    const el = this.bottomDetector()?.nativeElement;
    if (!el) return;
    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          this.isBottomReached.set(e.isIntersecting);
        });
      },
      { root: this.tableWrapper()?.nativeElement || null, threshold: 0.1, rootMargin: '0px' },
    );
    this._observer.observe(el);
  }

  loadMore() {
    this.facade.loadMore();
  }

  onSortChange(sort: Sort) {
    const columnMap: Record<string, string> = {
      deviceName: 'deviceName',
      user: 'userDisplayName',
      email: 'userPrincipalName',
      operatingSystem: 'operatingSystem',
      complianceState: 'complianceState',
      agent: 'agent',
    };
    const apiColumn = columnMap[sort.active];
    if (apiColumn) {
      const direction = (sort.direction === '' ? 'asc' : sort.direction) as 'asc' | 'desc';
      this._orderBy.set(apiColumn);
      this._order.set(direction);
      this.facade.setSort(apiColumn, direction);
    }
  }
}
